import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import mammoth from "mammoth";
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronRight,
  Clapperboard,
  FileText,
  Image as ImageIcon,
  Loader2,
  PackageCheck,
  Play,
  Plus,
  RefreshCw,
  Save,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Square,
  Trash2,
  Upload,
  Users,
  Video,
  WandSparkles,
} from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useToast } from "../../context/ToastContext";
import {
  IMAGE_AI_PROVIDERS,
  TEXT_AI_PROVIDERS,
  normalizeImageProvider,
  normalizeTextProvider,
} from "../../constants/globalAiProviders";
import { getCurrentImageProvider } from "../../utils/wanxImageCapabilities";
import { STYLE_OPTIONS } from "../storyboard/common/Constants";
import {
  getVideoProviderLabel,
  isOverseasVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoProvider,
  normalizeProjectVideoRatio,
} from "../../utils/projectRuntime";
import {
  queryTaskStatusByRequestId,
  startTaskPolling,
  submitTask,
} from "../../services/taskService";
import {
  analyzeNarrationAssets,
  completeNarrationStage,
  confirmNarrationAssetBible,
  confirmNarrationAssets,
  confirmNarrationEpisodes,
  confirmNarrationQa,
  confirmNarrationStoryboards,
  failNarrationStage,
  fetchNarrationWorkflow,
  previewNarrationEpisodes,
  releaseNarrationStage,
  reserveNarrationStage,
  resumeNarrationWorkflow,
  saveNarrationAssetBible,
  saveNarrationSource,
  startNarrationStage,
  stopNarrationWorkflow,
  validateNarrationStoryboards,
} from "../../services/narrationProjectService";

const WORKFLOW_STATE_KEY = "narration_project_v3";
const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
const createTaskLeaseId = () => (
  globalThis.crypto?.randomUUID?.()
  || `narration-${Date.now()}-${Math.random().toString(36).slice(2)}`
);
const pendingTaskBindingKey = (projectId) => `narration_project_v3:pending_task_binding:${projectId}`;

const readPendingTaskBinding = (projectId) => {
  if (!projectId || typeof window === "undefined") return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(pendingTaskBindingKey(projectId)) || "null");
    if (!value || String(value.projectId) !== String(projectId)) return null;
    if (!value.leaseId || !value.stage || !value.taskType) return null;
    return {
      ...value,
      requestId: value.requestId || value.leaseId,
      taskId: value.taskId || "",
    };
  } catch {
    return null;
  }
};

const writePendingTaskBinding = (projectId, binding) => {
  if (!projectId || typeof window === "undefined") return;
  window.localStorage.setItem(
    pendingTaskBindingKey(projectId),
    JSON.stringify({ ...binding, projectId: String(projectId), savedAt: Date.now() }),
  );
};

const clearPendingTaskBinding = (projectId, expectedTaskId = "") => {
  if (!projectId || typeof window === "undefined") return;
  if (expectedTaskId) {
    const current = readPendingTaskBinding(projectId);
    if (current && String(current.taskId) !== String(expectedTaskId)) return;
  }
  window.localStorage.removeItem(pendingTaskBindingKey(projectId));
};

const STAGES = [
  { key: "source", number: "01", title: "导入剧本", description: "保存解说剧原稿", icon: FileText },
  { key: "episode_review", number: "02", title: "确认分集", description: "预览并确认入库范围", icon: BookOpen },
  { key: "asset_plan", number: "03", title: "资产规划", description: "识别角色变体与全剧资产", icon: Sparkles },
  { key: "asset_review", number: "04", title: "确认资产", description: "生成并检查角色、场景、道具", icon: PackageCheck },
  { key: "storyboard_review", number: "05", title: "确认分镜", description: "逐集检查小故事板提示词", icon: Clapperboard },
  { key: "qa_review", number: "06", title: "质量校验", description: "集中处理阻塞项与提醒", icon: ShieldCheck },
  { key: "delivery", number: "07", title: "生产交付", description: "续跑缺失视频并交付", icon: Video },
];

const STAGE_ORDER = {
  source: 0,
  episode_review: 1,
  asset_plan: 2,
  asset_preparation: 3,
  asset_review: 3,
  storyboard_preparation: 4,
  storyboard_review: 4,
  qa_review: 5,
  production: 6,
  delivery: 6,
};

const ASSET_GENERATION_PHASES = [
  { key: "character", label: "角色本体" },
  { key: "character_variant", label: "角色变体" },
  { key: "scene", label: "场景" },
  { key: "prop", label: "道具" },
];

const resolveAssetGenerationPhase = (text) => {
  const normalized = String(text || "").toLowerCase();
  if (normalized.includes("变体") || normalized.includes("variant")) return "character_variant";
  if (normalized.includes("场景") || normalized.includes("scene")) return "scene";
  if (normalized.includes("道具") || normalized.includes("prop")) return "prop";
  if (normalized.includes("角色") || normalized.includes("character")) return "character";
  return "";
};

const STAGE_TITLE = {
  source: "导入解说剧剧本",
  episode_review: "确认分集入库范围",
  asset_plan: "建立全剧资产与角色变体规划",
  asset_preparation: "生成角色、场景与道具",
  asset_review: "检查并确认已生成资产",
  storyboard_preparation: "为全部分集拆分小故事板",
  storyboard_review: "逐集检查小故事板",
  qa_review: "解说剧质量校验",
  production: "生成尚未完成的视频",
  delivery: "生产完成",
};

const TASK_STAGE_META = {
  asset_preparation: {
    taskType: "NOVEL_EXPLAINER_ASSET_PREPARE_WORKFLOW",
    nextStage: "asset_review",
    label: "生成资产",
  },
  storyboard_preparation: {
    taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
    nextStage: "storyboard_review",
    label: "拆分分镜",
  },
  production: {
    taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
    nextStage: "delivery",
    label: "生成视频",
  },
};

const normalizeList = (value) => (Array.isArray(value) ? value : []);
const safeObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});
const prettyJson = (value) => JSON.stringify(safeObject(value), null, 2);
const hasUsableAssetBible = (value) => (
  ["characters", "scenes", "props"].some((key) => (
    normalizeList(value?.[key]).some((item) => (
      String(item?.name || item?.title || "").trim().length > 0
    ))
  ))
);

const resolveUploadText = async (file) => {
  const fileName = String(file?.name || "").toLowerCase();
  if (fileName.endsWith(".docx")) {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return String(result?.value || "").trim();
  }
  return String(await file.text()).trim();
};

const getResultObject = (result) => {
  if (!result || typeof result !== "object") return result;
  if (result.result && typeof result.result === "object") return result.result;
  if (typeof result.resultPayload === "string" && result.resultPayload.trim()) {
    try {
      return JSON.parse(result.resultPayload);
    } catch {
      return result;
    }
  }
  return result;
};

const getTaskTerminalStatus = (result) => {
  const directStatus = String(result?.status || "").trim().toLowerCase();
  if (directStatus) return directStatus;
  const unwrapped = getResultObject(result);
  return String(unwrapped?.status || "").trim().toLowerCase();
};

const stageDisplayKey = (stage = "source") => {
  if (stage === "asset_preparation") return "asset_review";
  if (stage === "storyboard_preparation") return "storyboard_review";
  if (stage === "production") return "delivery";
  return stage;
};

const actionLabel = (action) => ({
  CREATE: "新增",
  UPDATE: "更新正文",
  UNCHANGED: "无变化",
  SKIP_EXISTING: "保留现有",
  SKIP_DUPLICATE: "跳过重复",
}[action] || action || "待处理");

const ActionButton = ({
  children,
  onClick,
  disabled = false,
  tone = "primary",
  className = "",
  title,
}) => {
  const style = tone === "primary"
    ? { backgroundColor: "var(--color-accent-primary)", color: "var(--color-accent-contrast)", borderColor: "var(--color-accent-primary)" }
    : tone === "danger"
      ? { color: "var(--color-error)", borderColor: "var(--color-error)" }
      : { color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)" };
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-10 items-center justify-center gap-2 rounded-md border px-4 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      style={style}
    >
      {children}
    </button>
  );
};

const Stat = ({ label, value, tone = "default" }) => {
  const toneColor = tone === "success"
    ? "var(--color-success)"
    : tone === "warning"
      ? "var(--color-warning)"
      : tone === "danger"
        ? "var(--color-error)"
        : "var(--color-text-primary)";
  return (
    <div className="min-w-[92px] border-l pl-4" style={{ borderColor: "var(--color-border-primary)" }}>
      <div className="text-[11px]" style={{ color: "var(--color-text-muted)" }}>{label}</div>
      <div className="mt-1 text-base font-semibold font-mono-num" style={{ color: toneColor }}>{value}</div>
    </div>
  );
};

const AssetEditorInput = ({ label, value, onChange, placeholder = "" }) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[10px]" style={{ color: "var(--color-text-muted)" }}>{label}</span>
    <input
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-9 w-full rounded-md border px-3 text-xs outline-none transition-colors focus:border-[var(--color-accent-primary)]"
      style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }}
    />
  </label>
);

const AssetEditorTextarea = ({ label, value, onChange, placeholder = "", rows = 3 }) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[10px]" style={{ color: "var(--color-text-muted)" }}>{label}</span>
    <textarea
      rows={rows}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full resize-y rounded-md border px-3 py-2 text-xs leading-5 outline-none transition-colors focus:border-[var(--color-accent-primary)]"
      style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }}
    />
  </label>
);

const AssetEditorTab = ({ active, children, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-10 items-center gap-2 border-b-2 px-4 text-xs font-semibold transition-colors"
    style={{
      borderColor: active ? "var(--color-accent-primary)" : "transparent",
      color: active ? "var(--color-text-primary)" : "var(--color-text-muted)",
      backgroundColor: active ? "color-mix(in srgb, var(--color-accent-primary) 7%, transparent)" : "transparent",
    }}
  >
    {children}
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-mono-num"
      style={{ color: active ? "var(--color-accent-primary)" : "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}
    >
      {count}
    </span>
  </button>
);

const StageRail = ({ workflow }) => {
  const currentIndex = STAGE_ORDER[workflow?.currentStage || "source"] ?? 0;
  const currentDisplay = stageDisplayKey(workflow?.currentStage);
  return (
    <aside className="w-[236px] shrink-0 overflow-y-auto border-r px-5 py-6 custom-scrollbar" style={{ borderColor: "var(--color-border-primary)" }}>
      <div className="text-[11px] font-semibold uppercase" style={{ color: "var(--color-text-muted)" }}>解说剧流程</div>
      <div className="mt-5">
        {STAGES.map((stage, index) => {
          const Icon = stage.icon;
          const active = currentDisplay === stage.key;
          const done = index < currentIndex || workflow?.status === "completed";
          return (
            <div key={stage.key} className="relative flex gap-3 pb-6 last:pb-0">
              {index < STAGES.length - 1 && (
                <span
                  className="absolute left-[15px] top-8 h-[calc(100%-22px)] w-px"
                  style={{ backgroundColor: done ? "var(--color-success)" : "var(--color-border-primary)" }}
                />
              )}
              <span
                className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border"
                style={done
                  ? { borderColor: "var(--color-success)", color: "var(--color-success)", backgroundColor: "color-mix(in srgb, var(--color-success) 12%, transparent)" }
                  : active
                    ? { borderColor: "var(--color-accent-primary)", color: "var(--color-accent-primary)", backgroundColor: "color-mix(in srgb, var(--color-accent-primary) 14%, transparent)" }
                    : { borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}
              >
                {done ? <Check size={15} /> : <Icon size={15} />}
              </span>
              <div className="min-w-0 pt-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono-num" style={{ color: active ? "var(--color-accent-primary)" : "var(--color-text-muted)" }}>{stage.number}</span>
                  <span className="text-sm font-semibold" style={{ color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)" }}>{stage.title}</span>
                </div>
                <p className="mt-1 text-[11px] leading-5" style={{ color: "var(--color-text-muted)" }}>{stage.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

const ProviderSelect = ({ label, value, onChange, options = [] }) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[11px]" style={{ color: "var(--color-text-muted)" }}>{label}</span>
    <AppSelect
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-md border px-3 text-xs outline-none"
      style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }}
    >
      {options.map((provider) => (
        <option key={provider.id} value={provider.id}>{provider.name} · {provider.recommendation}</option>
      ))}
    </AppSelect>
  </label>
);

const EmptyHint = ({ icon: Icon, title, description, action }) => (
  <div className="flex min-h-[280px] flex-col items-center justify-center border-y text-center" style={{ borderColor: "var(--color-border-primary)" }}>
    <Icon size={30} strokeWidth={1.4} style={{ color: "var(--color-text-muted)" }} />
    <div className="mt-4 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</div>
    <p className="mt-2 max-w-lg text-xs leading-6" style={{ color: "var(--color-text-muted)" }}>{description}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const isVariantCandidate = (item) => {
  if (!item) return false;
  if (item.isVariant === true || String(item.isVariant).toLowerCase() === "true") return true;
  const variantIndex = Number(item.variantIndex);
  return Number.isFinite(variantIndex) && variantIndex >= 0;
};

const AssetLinks = ({ projectId, assets, navigate }) => (
  <div className="grid grid-cols-3 border-y" style={{ borderColor: "var(--color-border-primary)" }}>
    {[
      { key: "characters", label: "角色", icon: Users, route: "character" },
      { key: "scenes", label: "场景", icon: ImageIcon, route: "scene" },
      { key: "props", label: "道具", icon: Sparkles, route: "prop" },
    ].map((item) => {
      const Icon = item.icon;
      const candidates = normalizeList(assets[item.key]);
      const variantCount = item.key === "characters"
        ? candidates.filter(isVariantCandidate).length
        : 0;
      const primaryCount = item.key === "characters"
        ? candidates.filter((candidate) => !isVariantCandidate(candidate)).length
        : candidates.length;
      return (
        <button
          key={item.key}
          type="button"
          onClick={() => navigate(`/project/${projectId}/assets/${item.route}`)}
          className="group flex min-h-[132px] items-center justify-between border-r px-6 text-left last:border-r-0"
          style={{ borderColor: "var(--color-border-primary)" }}
        >
          <div>
            <Icon size={19} style={{ color: "var(--color-accent-primary)" }} />
            <div className="mt-3 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{item.label}</div>
            <div className="mt-1 text-xl font-semibold font-mono-num" style={{ color: "var(--color-text-primary)" }}>{primaryCount}</div>
            {variantCount > 0 && (
              <div className="mt-1 text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                另有 {variantCount} 个形象变体
              </div>
            )}
          </div>
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" style={{ color: "var(--color-text-muted)" }} />
        </button>
      );
    })}
  </div>
);

const NarrationProjectWorkbench = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const pollStopRef = useRef(null);
  const mountedRef = useRef(true);
  const pollingTaskRef = useRef("");
  const bindingRecoveryTimerRef = useRef(null);
  const pollingRecoveryTimerRef = useRef(null);
  const attachTaskPollingRef = useRef(null);

  const [project, setProject] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [assets, setAssets] = useState({ characters: [], scenes: [], props: [] });
  const [sourceText, setSourceText] = useState("");
  const [sourceName, setSourceName] = useState("");
  const defaultTextProvider = normalizeTextProvider(localStorage.getItem("ai_provider"));
  const defaultImageProvider = getCurrentImageProvider();
  const [textProvider, setTextProvider] = useState(defaultTextProvider);
  const [imageProvider, setImageProvider] = useState(defaultImageProvider);
  const [styleAnchor, setStyleAnchor] = useState("modern_cdrama");
  const [importMode, setImportMode] = useState("merge");
  const [selectedEpisodeNumbers, setSelectedEpisodeNumbers] = useState([]);
  const [assetBibleText, setAssetBibleText] = useState("{}");
  const [assetBibleDirty, setAssetBibleDirty] = useState(false);
  const [assetEditorTab, setAssetEditorTab] = useState("characters");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [actionBusy, setActionBusy] = useState("");
  const [progress, setProgress] = useState({ progress: 0, text: "" });
  const [sourceDirty, setSourceDirty] = useState(false);
  const [validation, setValidation] = useState(null);
  const [bindingRecoveryVersion, setBindingRecoveryVersion] = useState(0);

  const refreshAssets = useCallback(async () => {
    if (!projectId) return;
    const candidateData = await authenticatedFetch(
      `${API_HOST}/api/assets/candidates?projectId=${projectId}`,
    );
    if (!mountedRef.current) return;
    if (candidateData) {
      setAssets({
        characters: normalizeList(candidateData?.characters),
        scenes: normalizeList(candidateData?.scenes),
        props: normalizeList(candidateData?.props),
      });
    }
  }, [projectId]);

  const refreshContext = useCallback(async ({ preserveSource = false, preserveBible = false } = {}) => {
    if (!projectId) return;
    const [projectList, nextWorkflow, episodeList, candidateData] = await Promise.all([
      authenticatedFetch(`${API_HOST}/api/project/list`).catch(() => []),
      fetchNarrationWorkflow(projectId),
      authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`).catch(() => []),
      authenticatedFetch(`${API_HOST}/api/assets/candidates?projectId=${projectId}`).catch(() => null),
    ]);
    if (!mountedRef.current) return;
    const nextProject = normalizeList(projectList).find((item) => String(item.id) === String(projectId)) || null;
    setProject(nextProject);
    setWorkflow(nextWorkflow);
    setEpisodes(normalizeList(episodeList));
    if (candidateData) {
      setAssets({
        characters: normalizeList(candidateData?.characters),
        scenes: normalizeList(candidateData?.scenes),
        props: normalizeList(candidateData?.props),
      });
    }
    if (["processing", "submitting", "analyzing"].includes(nextWorkflow?.status)) {
      setProgress({
        progress: Number.isFinite(Number(nextWorkflow?.progress)) ? Number(nextWorkflow.progress) : 0,
        text: nextWorkflow?.stageText || nextWorkflow?.currentStageText || "任务执行中",
      });
    }
    setValidation(nextWorkflow?.qaResult || nextWorkflow?.validationResult || null);
    if (!preserveSource) {
      setSourceText(nextWorkflow?.sourceText || "");
      setSourceName(nextWorkflow?.sourceName || "");
      setTextProvider(normalizeTextProvider(nextWorkflow?.textProvider || defaultTextProvider));
      setImageProvider(normalizeImageProvider(nextWorkflow?.imageProvider || defaultImageProvider));
      setStyleAnchor(nextWorkflow?.styleAnchor || nextProject?.visualStyle || "modern_cdrama");
      setSourceDirty(false);
    }
    if (!preserveBible) {
      setAssetBibleText(prettyJson(nextWorkflow?.assetBible));
      setAssetBibleDirty(false);
    }
  }, [defaultImageProvider, defaultTextProvider, projectId]);

  const bindSubmittedTask = useCallback(async (binding, maxAttempts = 3) => {
    let lastError = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        return await startNarrationStage(projectId, {
          stage: binding.stage,
          leaseId: binding.leaseId,
          taskId: binding.taskId,
          taskType: binding.taskType,
          textProvider: binding.textProvider,
          imageProvider: binding.imageProvider,
          styleAnchor: binding.styleAnchor,
        });
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts - 1) await wait(500 * (attempt + 1));
      }
    }
    throw lastError || new Error("任务状态绑定失败");
  }, [projectId]);

  const resolvePendingTaskBinding = useCallback(async (binding, maxAttempts = 1) => {
    if (binding?.taskId) return binding;
    const requestId = String(binding?.requestId || binding?.leaseId || "").trim();
    if (!requestId) return null;

    let lastError = null;
    let receivedStatus = false;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const status = await queryTaskStatusByRequestId(requestId);
        receivedStatus = true;
        if (String(status?.status || "").toLowerCase() !== "not_found") {
          const resolved = {
            ...binding,
            requestId,
            taskId: String(status?.taskId || requestId).trim(),
          };
          if (!resolved.taskId) return null;
          writePendingTaskBinding(projectId, resolved);
          return resolved;
        }
      } catch (error) {
        lastError = error;
      }
      if (attempt < maxAttempts - 1) await wait(500 * (attempt + 1));
    }
    if (!receivedStatus && lastError) throw lastError;
    return null;
  }, [projectId]);

  const ensureSubmittedTaskBound = useCallback(async (stage, taskId) => {
    const pending = readPendingTaskBinding(projectId);
    if (!pending
      || String(pending.taskId) !== String(taskId)
      || String(pending.stage) !== String(stage)) {
      return null;
    }
    const nextWorkflow = await bindSubmittedTask(pending);
    clearPendingTaskBinding(projectId, taskId);
    if (mountedRef.current) setWorkflow(nextWorkflow);
    return nextWorkflow;
  }, [bindSubmittedTask, projectId]);

  const finishTrackedTask = useCallback(async (stage, taskId, result) => {
    const meta = TASK_STAGE_META[stage];
    if (!meta) return;
    try {
      if (pollingRecoveryTimerRef.current) {
        window.clearTimeout(pollingRecoveryTimerRef.current);
        pollingRecoveryTimerRef.current = null;
      }
      const terminalStatus = getTaskTerminalStatus(result);
      if (["stopped", "canceled", "cancelled"].includes(terminalStatus)) {
        pollingTaskRef.current = "";
        clearPendingTaskBinding(projectId, taskId);
        await refreshContext({ preserveSource: true, preserveBible: true });
        await refreshAssets().catch(() => {});
        if (!mountedRef.current) return;
        const stoppedResult = safeObject(result);
        setProgress({
          progress: Number.isFinite(Number(stoppedResult.progress))
            ? Number(stoppedResult.progress)
            : 0,
          text: stoppedResult.stageText
            || stoppedResult.currentStageText
            || "流程已停止",
        });
        toast.info("流程已停止，已经生成的资产和当前进度均已保留。");
        return;
      }
      await ensureSubmittedTaskBound(stage, taskId);
      const nextWorkflow = await completeNarrationStage(projectId, {
        taskId,
        nextStage: meta.nextStage,
        result: getResultObject(result),
      });
      if (!mountedRef.current) return;
      pollingTaskRef.current = "";
      clearPendingTaskBinding(projectId, taskId);
      setWorkflow(nextWorkflow);
      const productionStillRunning = stage === "production"
        && nextWorkflow?.currentStage === "production"
        && nextWorkflow?.status !== "completed";
      setProgress(productionStillRunning
        ? { progress: 95, text: "视频仍在后台生成中" }
        : { progress: 100, text: `${meta.label}完成` });
      await refreshContext({ preserveSource: true, preserveBible: true });
      await refreshAssets().catch(() => {});
      if (productionStillRunning) {
        toast.info("部分视频仍在后台生成，原任务已保留，稍后可继续检查。");
      } else {
        toast.success(`${meta.label}完成，请检查结果`);
      }
    } catch (error) {
      toast.error(`任务完成，但流程状态保存失败：${error.message}`, 0);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [ensureSubmittedTaskBound, projectId, refreshAssets, refreshContext, toast]);

  const failTrackedTask = useCallback(async (stage, taskId, message) => {
    try {
      if (pollingRecoveryTimerRef.current) {
        window.clearTimeout(pollingRecoveryTimerRef.current);
        pollingRecoveryTimerRef.current = null;
      }
      await ensureSubmittedTaskBound(stage, taskId);
      const nextWorkflow = await failNarrationStage(projectId, {
        taskId,
        errorMessage: message || `${TASK_STAGE_META[stage]?.label || "任务"}失败`,
      });
      if (!mountedRef.current) return;
      pollingTaskRef.current = "";
      clearPendingTaskBinding(projectId, taskId);
      setWorkflow(nextWorkflow);
      setProgress((previous) => ({
        progress: Number.isFinite(Number(nextWorkflow?.progress))
          ? Number(nextWorkflow.progress)
          : previous.progress,
        text: message || "任务执行失败",
      }));
      await refreshAssets().catch(() => {});
      toast.error(message || "任务执行失败", 0);
    } catch (error) {
      if (!String(error?.message || "").includes("租约已失效")) {
        toast.error(`${message || "任务执行失败"}；状态保存失败：${error.message}`, 0);
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [ensureSubmittedTaskBound, projectId, refreshAssets, toast]);

  const attachTaskPolling = useCallback((stage, taskId) => {
    if (!stage || !taskId || pollingTaskRef.current === taskId) return;
    if (pollingRecoveryTimerRef.current) {
      window.clearTimeout(pollingRecoveryTimerRef.current);
      pollingRecoveryTimerRef.current = null;
    }
    pollStopRef.current?.();
    pollingTaskRef.current = taskId;
    setSubmitting(true);
    pollStopRef.current = startTaskPolling(taskId, {
      maxTotalMs: 6 * 60 * 60 * 1000,
      onProgress: (next) => {
        if (!mountedRef.current) return;
        setProgress({
          progress: Number.isFinite(Number(next?.progress)) ? Number(next.progress) : 0,
          text: next?.currentStageText || TASK_STAGE_META[stage]?.label || "处理中",
        });
      },
      onSuccess: (result) => finishTrackedTask(stage, taskId, result),
      onError: (message) => failTrackedTask(stage, taskId, message),
      onPollingInterrupted: (message) => {
        if (!mountedRef.current) return;
        pollStopRef.current = null;
        pollingTaskRef.current = "";
        setSubmitting(false);
        setProgress({
          progress: 0,
          text: message || "后台任务仍在执行，正在恢复查询",
        });
        toast.info("进度查询暂时中断，后台任务未被判定失败，稍后会自动恢复。");
        pollingRecoveryTimerRef.current = window.setTimeout(() => {
          pollingRecoveryTimerRef.current = null;
          if (!mountedRef.current) return;
          attachTaskPollingRef.current?.(stage, taskId);
        }, 15000);
      },
    });
  }, [failTrackedTask, finishTrackedTask, toast]);
  attachTaskPollingRef.current = attachTaskPolling;

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    refreshContext()
      .catch((error) => toast.error(`解说剧生产台加载失败：${error.message}`, 0))
      .finally(() => mountedRef.current && setLoading(false));
    return () => {
      mountedRef.current = false;
      pollStopRef.current?.();
      pollStopRef.current = null;
      pollingTaskRef.current = "";
      if (bindingRecoveryTimerRef.current) {
        window.clearTimeout(bindingRecoveryTimerRef.current);
        bindingRecoveryTimerRef.current = null;
      }
      if (pollingRecoveryTimerRef.current) {
        window.clearTimeout(pollingRecoveryTimerRef.current);
        pollingRecoveryTimerRef.current = null;
      }
    };
  }, [refreshContext, toast]);

  useEffect(() => {
    if (workflow?.status === "processing" && workflow?.activeTaskId && workflow?.currentStage) {
      attachTaskPolling(workflow.currentStage, workflow.activeTaskId);
    }
  }, [attachTaskPolling, workflow?.activeTaskId, workflow?.currentStage, workflow?.status]);

  useEffect(() => {
    const shouldRefresh = workflow?.currentStage === "asset_preparation"
      && ["processing", "submitting"].includes(workflow?.status);
    if (!shouldRefresh) return undefined;

    refreshAssets().catch(() => {});
    const timer = window.setInterval(() => {
      refreshAssets().catch(() => {});
    }, 3500);
    return () => window.clearInterval(timer);
  }, [refreshAssets, workflow?.currentStage, workflow?.status]);

  useEffect(() => {
    if (workflow?.status !== "analyzing" || !workflow?.activeTaskId) return undefined;
    let cancelled = false;
    let timer = null;

    const recoverAssetAnalysis = async () => {
      try {
        const nextWorkflow = await fetchNarrationWorkflow(projectId);
        if (cancelled || !mountedRef.current) return;
        setWorkflow(nextWorkflow);
        if (nextWorkflow?.status === "analyzing") {
          timer = window.setTimeout(recoverAssetAnalysis, 3000);
          return;
        }
        setAssetBibleText(prettyJson(nextWorkflow?.assetBible));
        setAssetBibleDirty(false);
        if (nextWorkflow?.errorMessage) {
          toast.error(`资产规划失败：${nextWorkflow.errorMessage}`, 0);
        } else {
          toast.success("全剧资产与角色变体分析完成");
        }
      } catch {
        if (!cancelled) {
          timer = window.setTimeout(recoverAssetAnalysis, 5000);
        }
      }
    };

    timer = window.setTimeout(recoverAssetAnalysis, 1500);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [projectId, toast, workflow?.activeTaskId, workflow?.status]);

  useEffect(() => {
    if (!workflow) return undefined;
    const storedPending = readPendingTaskBinding(projectId);
    const pending = storedPending || (
      workflow.status === "submitting"
      && workflow.activeTaskId
      && workflow.currentStage
      && workflow.activeTaskType
        ? {
          projectId: String(projectId),
          stage: workflow.currentStage,
          leaseId: workflow.activeTaskId,
          requestId: workflow.activeTaskId,
          taskId: "",
          taskType: workflow.activeTaskType,
          textProvider: workflow.textProvider || textProvider,
          imageProvider: workflow.imageProvider || imageProvider,
          styleAnchor: workflow.styleAnchor || styleAnchor,
        }
        : null
    );
    if (!pending) return undefined;

    if (workflow.status === "processing" && workflow.activeTaskId) {
      clearPendingTaskBinding(projectId);
      attachTaskPolling(workflow.currentStage || pending.stage, workflow.activeTaskId);
      return undefined;
    }

    const matchesLease = workflow.status === "submitting"
      && String(workflow.activeTaskId) === String(pending.leaseId)
      && String(workflow.currentStage) === String(pending.stage);
    if (!matchesLease) {
      if (workflow.status !== "submitting" && workflow.status !== "processing") {
        clearPendingTaskBinding(projectId);
      }
      return undefined;
    }

    let cancelled = false;
    let notFoundAttempts = 0;
    const recover = async () => {
      try {
        setSubmitting(true);
        setProgress({
          progress: 0,
          text: `正在恢复${TASK_STAGE_META[pending.stage]?.label || "任务"}状态`,
        });
        const resolvedBinding = await resolvePendingTaskBinding(pending, 1);
        if (!resolvedBinding) {
          notFoundAttempts += 1;
          if (notFoundAttempts < 12) {
            bindingRecoveryTimerRef.current = window.setTimeout(recover, 5000);
            return;
          }
          const releasedWorkflow = await releaseNarrationStage(projectId, {
            leaseId: pending.leaseId,
            errorMessage: "未找到已提交任务，已释放本次提交占位",
          });
          if (cancelled || !mountedRef.current) return;
          clearPendingTaskBinding(projectId);
          setWorkflow(releasedWorkflow);
          setSubmitting(false);
          setProgress({ progress: 0, text: "上次任务未实际提交，可重新开始" });
          toast.warning("未找到上次提交的后台任务，已恢复为可重新开始状态。");
          return;
        }
        const nextWorkflow = await bindSubmittedTask(resolvedBinding, 1);
        if (cancelled || !mountedRef.current) return;
        clearPendingTaskBinding(projectId, resolvedBinding.taskId);
        setWorkflow(nextWorkflow);
        attachTaskPolling(resolvedBinding.stage, resolvedBinding.taskId);
      } catch {
        if (cancelled || !mountedRef.current) return;
        bindingRecoveryTimerRef.current = window.setTimeout(recover, 5000);
      }
    };
    recover();
    return () => {
      cancelled = true;
      if (bindingRecoveryTimerRef.current) {
        window.clearTimeout(bindingRecoveryTimerRef.current);
        bindingRecoveryTimerRef.current = null;
      }
    };
  }, [
    attachTaskPolling,
    bindSubmittedTask,
    bindingRecoveryVersion,
    imageProvider,
    projectId,
    resolvePendingTaskBinding,
    styleAnchor,
    textProvider,
    toast,
    workflow,
  ]);

  const episodePreview = useMemo(
    () => safeObject(workflow?.episodePlan?.preview),
    [workflow?.episodePlan?.preview],
  );
  const previewRows = useMemo(
    () => normalizeList(episodePreview.episodes),
    [episodePreview.episodes],
  );
  useEffect(() => {
    if (!previewRows.length) return;
    setImportMode(workflow?.episodePlan?.mode || episodePreview.mode || "merge");
    setSelectedEpisodeNumbers(previewRows
      .filter((item) => item.selectedByDefault)
      .map((item) => item.targetEpisodeNumber)
      .filter((value) => Number.isFinite(Number(value))));
  }, [episodePreview.mode, previewRows, workflow?.episodePlan?.mode, workflow?.episodePlanRevision]);

  const currentStage = workflow?.currentStage || "source";
  const currentIndex = STAGE_ORDER[currentStage] ?? 0;
  const isProcessing = workflow?.status === "processing"
    || workflow?.status === "submitting"
    || workflow?.status === "analyzing"
    || submitting;
  const isStopped = workflow?.status === "stopped";
  const isOverseas = project?.projectMarket === "overseas"
    || project?.contentLanguage === "en-US"
    || isOverseasVideoProvider(project?.videoProvider);
  const ratio = normalizeProjectVideoRatio(
    project?.defaultVideoRatio || project?.videoRatio || project?.defaultAspectRatio,
  );
  const configuredVideoProvider = normalizeVideoProvider(project?.videoProvider);
  const videoProvider = isSeedanceVideoProvider(configuredVideoProvider)
    ? configuredVideoProvider
    : "";
  const videoModel = isOverseas
    ? "dreamina-seedance-2-0-fast"
    : "doubao-seedance-2-0-fast-260128";
  const videoResolution = "480p";
  const episodeIds = useMemo(() => episodes.map((episode) => episode.id).filter(Boolean), [episodes]);
  const currentValidation = validation || workflow?.qaResult || workflow?.validationResult || {};
  const segmentTotal = Number(currentValidation?.segmentCount || 0);
  const readyEpisodeCount = Number(currentValidation?.readyEpisodeCount || 0);
  const blockingCount = Number(currentValidation?.blockingCount || 0);
  const warningCount = Number(currentValidation?.warningCount || 0);
  const assetBible = useMemo(() => {
    try {
      return safeObject(JSON.parse(assetBibleText || "{}"));
    } catch {
      return safeObject(workflow?.assetBible);
    }
  }, [assetBibleText, workflow?.assetBible]);
  const plannedVariantCount = useMemo(
    () => normalizeList(assetBible?.characters)
      .reduce(
        (sum, character) => sum + normalizeList(character?.variants)
          .filter((variant) => variant?.requiresImage !== false).length,
        0,
      ),
    [assetBible],
  );
  const plannedVariantLabels = useMemo(
    () => normalizeList(assetBible?.characters).flatMap((character) => {
      const characterName = String(character?.name || "未命名角色").trim();
      return normalizeList(character?.variants)
        .filter((variant) => variant?.requiresImage !== false)
        .map((variant) => `${characterName} · ${String(variant?.name || variant?.title || "未命名变体").trim()}`);
    }),
    [assetBible],
  );
  const generatedVariantCount = useMemo(
    () => normalizeList(assets.characters)
      .filter((item) => isVariantCandidate(item) && String(item?.imageUrl || "").trim()).length,
    [assets.characters],
  );
  const plannedAssetCounts = useMemo(() => ({
    characters: normalizeList(assetBible?.characters).length,
    scenes: normalizeList(assetBible?.scenes).length,
    props: normalizeList(assetBible?.props).length,
  }), [assetBible]);
  const generatedAssetCounts = useMemo(() => ({
    characters: normalizeList(assets.characters)
      .filter((item) => !isVariantCandidate(item) && String(item?.imageUrl || "").trim()).length,
    scenes: normalizeList(assets.scenes)
      .filter((item) => String(item?.imageUrl || "").trim()).length,
    props: normalizeList(assets.props)
      .filter((item) => String(item?.imageUrl || "").trim()).length,
  }), [assets]);
  const generatedAssetTotal = generatedAssetCounts.characters
    + generatedAssetCounts.scenes
    + generatedAssetCounts.props;
  const missingVariantCount = Math.max(0, plannedVariantCount - generatedVariantCount);
  const assetGenerationSteps = useMemo(() => {
    const countByPhase = {
      character: {
        generated: generatedAssetCounts.characters,
        planned: plannedAssetCounts.characters,
      },
      character_variant: {
        generated: generatedVariantCount,
        planned: plannedVariantCount,
      },
      scene: {
        generated: generatedAssetCounts.scenes,
        planned: plannedAssetCounts.scenes,
      },
      prop: {
        generated: generatedAssetCounts.props,
        planned: plannedAssetCounts.props,
      },
    };
    const messagePhase = resolveAssetGenerationPhase(
      progress.text || workflow?.stageText || workflow?.currentStageText,
    );
    const inferredPhase = ASSET_GENERATION_PHASES.find(({ key }) => {
      const counts = countByPhase[key];
      return counts.planned > counts.generated;
    })?.key || "";
    const activePhase = isProcessing ? (messagePhase || inferredPhase) : "";
    const activeIndex = ASSET_GENERATION_PHASES.findIndex(({ key }) => key === activePhase);

    return ASSET_GENERATION_PHASES.map((phase, index) => {
      const counts = countByPhase[phase.key];
      const complete = counts.planned === 0 || counts.generated >= counts.planned;
      const alreadyProcessed = activeIndex > index;
      return {
        ...phase,
        ...counts,
        active: activePhase === phase.key,
        complete,
        warning: alreadyProcessed && !complete,
      };
    });
  }, [
    generatedAssetCounts.characters,
    generatedAssetCounts.props,
    generatedAssetCounts.scenes,
    generatedVariantCount,
    isProcessing,
    plannedAssetCounts.characters,
    plannedAssetCounts.props,
    plannedAssetCounts.scenes,
    plannedVariantCount,
    progress.text,
    workflow?.currentStageText,
    workflow?.stageText,
  ]);
  const assetGenerationResult = useMemo(() => {
    const directResult = safeObject(workflow?.assetResult?.assetGeneration);
    if (Object.keys(directResult).length > 0) return directResult;
    const firstEpisode = normalizeList(workflow?.assetResult?.episodes)[0];
    return safeObject(firstEpisode?.assetResult);
  }, [workflow?.assetResult]);
  const characterVariantIssues = useMemo(
    () => normalizeList(assetGenerationResult?.characterVariantIssues)
      .map((issue) => String(issue || "").trim())
      .filter(Boolean),
    [assetGenerationResult],
  );
  const variantPlaceholderWarning = String(
    workflow?.assetResult?.variantPlaceholderWarning || "",
  ).trim();
  const mutateAssetBible = (mutator) => {
    const draft = JSON.parse(JSON.stringify(assetBible || {}));
    ["characters", "scenes", "props", "unresolved", "warnings"].forEach((key) => {
      if (!Array.isArray(draft[key])) draft[key] = [];
    });
    mutator(draft);
    setAssetBibleText(prettyJson(draft));
    setAssetBibleDirty(true);
  };
  const updateAssetItem = (collection, index, field, value) => {
    mutateAssetBible((draft) => {
      draft[collection][index] = { ...safeObject(draft[collection][index]), [field]: value };
    });
  };
  const addAssetItem = (collection) => {
    const templates = {
      characters: { name: "", aliases: [], identity: "", basePrompt: "", variants: [] },
      scenes: { name: "", description: "", imagePrompt: "", variants: [] },
      props: { name: "", owner: "", description: "", imagePrompt: "", variants: [] },
    };
    mutateAssetBible((draft) => {
      draft[collection].push(templates[collection]);
    });
  };
  const removeAssetItem = (collection, index) => {
    mutateAssetBible((draft) => {
      draft[collection].splice(index, 1);
    });
  };
  const updateCharacterVariant = (characterIndex, variantIndex, field, value) => {
    mutateAssetBible((draft) => {
      const character = safeObject(draft.characters[characterIndex]);
      const variants = normalizeList(character.variants).map((item) => ({ ...safeObject(item) }));
      variants[variantIndex] = { ...safeObject(variants[variantIndex]), [field]: value };
      draft.characters[characterIndex] = { ...character, variants };
    });
  };
  const addCharacterVariant = (characterIndex) => {
    mutateAssetBible((draft) => {
      const character = safeObject(draft.characters[characterIndex]);
      const variants = normalizeList(character.variants).map((item) => ({ ...safeObject(item) }));
      variants.push({
        name: "",
        type: "state",
        trigger: "",
        appearancePrompt: "",
        requiresImage: true,
      });
      draft.characters[characterIndex] = { ...character, variants };
    });
  };
  const removeCharacterVariant = (characterIndex, variantIndex) => {
    mutateAssetBible((draft) => {
      const character = safeObject(draft.characters[characterIndex]);
      const variants = normalizeList(character.variants).map((item) => ({ ...safeObject(item) }));
      variants.splice(variantIndex, 1);
      draft.characters[characterIndex] = { ...character, variants };
    });
  };
  const updateReviewItem = (collection, index, value) => {
    mutateAssetBible((draft) => {
      draft[collection][index] = value;
    });
  };
  const addReviewItem = (collection) => {
    mutateAssetBible((draft) => {
      draft[collection].push("");
    });
  };
  const removeReviewItem = (collection, index) => {
    mutateAssetBible((draft) => {
      draft[collection].splice(index, 1);
    });
  };

  const buildCommonPayload = useCallback(() => ({
    projectId: Number(projectId),
    workflowStateKey: WORKFLOW_STATE_KEY,
    workflowMode: "novel_explainer",
    storyMode: "narration",
    narrationMode: true,
    styleAnchor,
    textProvider,
    imageProvider,
    aspectRatio: ratio,
    videoProvider,
    videoModel,
    resolution: videoResolution,
    segmentSeconds: 15,
    fullAuto: false,
  }), [imageProvider, projectId, ratio, styleAnchor, textProvider, videoModel, videoProvider, videoResolution]);

  const startStageTask = useCallback(async (stage) => {
    if (isProcessing || isStopped) return;
    if (!isSeedanceVideoProvider(videoProvider)) {
      toast.warning("项目视频厂商配置尚未加载，请刷新项目后重试");
      return;
    }
    const meta = TASK_STAGE_META[stage];
    if (!meta) return;
    if (!episodeIds.length) {
      toast.warning("请先确认并保存至少一个分集");
      return;
    }
    setSubmitting(true);
    setProgress({ progress: 0, text: `正在提交${meta.label}任务` });
    const leaseId = createTaskLeaseId();
    let reserved = false;
    let submittedTaskId = "";
    try {
      const common = buildCommonPayload();
      const payload = stage === "asset_preparation"
        ? {
          ...common,
          episodeIds,
          preferExistingEpisodes: true,
          useExistingEpisodesOnly: true,
          generateAssets: true,
          generateVideos: false,
          waitForVideos: false,
          outputStyle: `${STYLE_OPTIONS.find((item) => item.value === styleAnchor)?.label || "解说剧"}解说剧`,
          scriptTitle: sourceName || project?.name || "解说剧生产",
          assetWorkflowStyle: "explainer-comic-assets",
          assetBible: workflow?.assetBible || {},
          targetEpisodes: episodeIds.length,
          maxEpisodes: episodeIds.length,
        }
        : {
          ...common,
          episodeIds,
          preferExistingEpisodes: true,
          useExistingEpisodesOnly: true,
          regenerateMiniStoryboard: false,
          generateAssets: false,
          generateVideos: stage === "production",
          waitForVideos: stage === "production",
          skipExistingVideos: true,
          prepareCharacterAssets: false,
        };
      const reservedWorkflow = await reserveNarrationStage(projectId, {
        stage,
        leaseId,
        taskType: meta.taskType,
        textProvider,
        imageProvider,
        styleAnchor,
      });
      reserved = true;
      if (mountedRef.current) setWorkflow(reservedWorkflow);

      const pendingBinding = {
        stage,
        leaseId,
        requestId: leaseId,
        taskId: "",
        taskType: meta.taskType,
        textProvider,
        imageProvider,
        styleAnchor,
      };
      writePendingTaskBinding(projectId, pendingBinding);
      const task = await submitTask(meta.taskType, payload, {
        projectId: Number(projectId),
        provider: textProvider,
        styleAnchor,
        source: "narration_project_v3",
        requestId: leaseId,
      });
      submittedTaskId = String(task?.taskId || "").trim();
      if (!submittedTaskId) {
        throw new Error("任务提交成功但未返回 taskId");
      }
      const submittedBinding = {
        ...pendingBinding,
        taskId: submittedTaskId,
      };
      writePendingTaskBinding(projectId, submittedBinding);
      const nextWorkflow = await bindSubmittedTask(submittedBinding);
      clearPendingTaskBinding(projectId, submittedTaskId);
      if (!mountedRef.current) return;
      setWorkflow(nextWorkflow);
      attachTaskPolling(stage, submittedTaskId);
    } catch (error) {
      setSubmitting(false);
      let recoveredBinding = null;
      try {
        recoveredBinding = await resolvePendingTaskBinding({
          stage,
          leaseId,
          requestId: leaseId,
          taskId: submittedTaskId,
          taskType: meta.taskType,
          textProvider,
          imageProvider,
          styleAnchor,
        }, 4);
      } catch (recoveryError) {
        console.warn("[NarrationProject] task lookup temporarily unavailable", recoveryError);
      }
      if (recoveredBinding?.taskId) {
        submittedTaskId = recoveredBinding.taskId;
        writePendingTaskBinding(projectId, recoveredBinding);
        try {
          const nextWorkflow = await bindSubmittedTask(recoveredBinding);
          clearPendingTaskBinding(projectId, recoveredBinding.taskId);
          if (mountedRef.current) setWorkflow(nextWorkflow);
        } catch (bindingError) {
          console.warn("[NarrationProject] recovered task binding pending", bindingError);
        }
        attachTaskPolling(stage, recoveredBinding.taskId);
        setBindingRecoveryVersion((current) => current + 1);
        console.error("[NarrationProject] task binding recovery pending", {
          stage,
          taskId: recoveredBinding.taskId,
          error,
        });
        toast.error("任务已经开始，正在恢复进度显示，请勿重复点击。", 0);
      } else {
        if (reserved) {
          setBindingRecoveryVersion((current) => current + 1);
          setProgress({
            progress: 0,
            text: "正在确认任务是否已提交，请勿重复点击",
          });
          toast.error(`任务提交响应异常，正在核对后台状态：${error.message}`, 0);
        } else {
          clearPendingTaskBinding(projectId);
          toast.error(`${meta.label}任务提交失败：${error.message}`, 0);
        }
      }
    }
  }, [
    attachTaskPolling,
    bindSubmittedTask,
    buildCommonPayload,
    episodeIds,
    imageProvider,
    isProcessing,
    isStopped,
    project?.name,
    projectId,
    resolvePendingTaskBinding,
    sourceName,
    styleAnchor,
    textProvider,
    toast,
    workflow?.assetBible,
  ]);

  const handleSaveSource = async () => {
    if (!sourceText.trim()) {
      toast.warning("请先输入解说剧剧本");
      return false;
    }
    setSaving(true);
    try {
      const nextWorkflow = await saveNarrationSource(projectId, {
        sourceText,
        sourceName,
        textProvider,
        imageProvider,
        styleAnchor,
      });
      setWorkflow(nextWorkflow);
      setSourceDirty(false);
      setSelectedEpisodeNumbers([]);
      setAssetBibleText("{}");
      setAssetBibleDirty(false);
      toast.success("剧本已保存，下一步请确认分集");
      return true;
    } catch (error) {
      toast.error(`剧本保存失败：${error.message}`, 0);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await resolveUploadText(file);
      if (!text) throw new Error("文件中没有可读取的文本");
      setSourceText(text);
      setSourceName(file.name);
      setSourceDirty(true);
      toast.success(`已读取 ${file.name}`);
    } catch (error) {
      toast.error(`文件读取失败：${error.message}`, 0);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handlePreviewEpisodes = async () => {
    setActionBusy("episodes-preview");
    try {
      if (sourceDirty) {
        const saved = await handleSaveSource();
        if (!saved) return;
      }
      const nextWorkflow = await previewNarrationEpisodes(projectId, { mode: importMode, sourceName });
      setWorkflow(nextWorkflow);
      toast.success("分集预览已生成，请确认需要写入的集数");
    } catch (error) {
      toast.error(`分集预览失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleConfirmEpisodes = async () => {
    setActionBusy("episodes-confirm");
    try {
      const nextWorkflow = await confirmNarrationEpisodes(projectId, {
        mode: importMode,
        selectedEpisodeNumbers,
      });
      setWorkflow(nextWorkflow);
      await refreshContext({ preserveSource: true });
      toast.success("分集已确认，可以开始规划全剧资产");
    } catch (error) {
      toast.error(`确认分集失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleAnalyzeBible = async () => {
    setActionBusy("asset-analyze");
    try {
      const nextWorkflow = await analyzeNarrationAssets(projectId, {
        textProvider,
        requestId: createTaskLeaseId(),
      });
      setWorkflow(nextWorkflow);
      if (nextWorkflow?.status === "analyzing") {
        toast.info("全剧资产与角色变体正在分析，请勿重复提交");
        return;
      }
      setAssetBibleText(prettyJson(nextWorkflow?.assetBible));
      setAssetBibleDirty(false);
      toast.success("全剧资产与角色变体分析完成");
    } catch (error) {
      toast.error(`资产规划失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const saveBible = async () => {
    let parsed;
    try {
      parsed = JSON.parse(assetBibleText);
    } catch (error) {
      console.error("[NarrationProject] invalid asset bible draft", error);
      toast.error("资产规划内容暂时无法保存，请刷新后重试", 0);
      return null;
    }
    if (!hasUsableAssetBible(parsed)) {
      toast.warning("请至少保留一个有名称的角色、场景或道具");
      return null;
    }
    setActionBusy("asset-save");
    try {
      const nextWorkflow = await saveNarrationAssetBible(
        projectId,
        parsed,
        workflow?.sourceRevision,
      );
      setWorkflow(nextWorkflow);
      setAssetBibleText(prettyJson(nextWorkflow?.assetBible));
      setAssetBibleDirty(false);
      toast.success("资产规划已保存");
      return nextWorkflow;
    } catch (error) {
      toast.error(`资产规划保存失败：${error.message}`, 0);
      return null;
    } finally {
      setActionBusy("");
    }
  };

  const handleConfirmBible = async () => {
    if (assetBibleDirty && !await saveBible()) return;
    setActionBusy("asset-confirm");
    try {
      const nextWorkflow = await confirmNarrationAssetBible(projectId);
      setWorkflow(nextWorkflow);
      toast.success("资产规划已确认，可以生成资产");
    } catch (error) {
      toast.error(`资产规划确认失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleConfirmAssets = async () => {
    setActionBusy("assets-confirm");
    try {
      const nextWorkflow = await confirmNarrationAssets(projectId);
      setWorkflow(nextWorkflow);
      toast.success("资产已确认，可以拆分小故事板");
    } catch (error) {
      toast.error(`资产确认失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleValidateStoryboards = async () => {
    setActionBusy("storyboard-validate");
    try {
      const result = await validateNarrationStoryboards(projectId);
      setValidation(result);
      setWorkflow((previous) => {
        if (!previous) return previous;
        const enteringReview = previous.currentStage === "storyboard_preparation";
        return {
          ...previous,
          currentStage: enteringReview ? "storyboard_review" : previous.currentStage,
          status: enteringReview ? "waiting_confirmation" : previous.status,
          validationResult: result,
          ...(previous.currentStage === "qa_review" ? { qaResult: result } : {}),
        };
      });
      if (result?.hasBlockingErrors) {
        toast.warning(`校验完成：${result.blockingCount || 0} 个阻塞项，${result.warningCount || 0} 个提醒`);
      } else {
        toast.success(`校验通过，共 ${result.segmentCount || 0} 个片段`);
      }
    } catch (error) {
      toast.error(`分镜校验失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleConfirmStoryboards = async () => {
    setActionBusy("storyboard-confirm");
    try {
      const nextWorkflow = await confirmNarrationStoryboards(projectId);
      setWorkflow(nextWorkflow);
      setValidation(nextWorkflow?.qaResult || nextWorkflow?.validationResult || currentValidation);
      toast.success("分镜已确认，进入集中质量校验");
    } catch (error) {
      toast.error(`分镜确认失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleConfirmQa = async () => {
    setActionBusy("qa-confirm");
    try {
      const nextWorkflow = await confirmNarrationQa(projectId);
      setWorkflow(nextWorkflow);
      setValidation(nextWorkflow?.qaResult || nextWorkflow?.validationResult || currentValidation);
      toast.success("质量校验已确认，可以生成视频");
    } catch (error) {
      toast.error(`质量校验确认失败：${error.message}`, 0);
    } finally {
      setActionBusy("");
    }
  };

  const handleStop = async () => {
    pollStopRef.current?.();
    pollingTaskRef.current = "";
    if (pollingRecoveryTimerRef.current) {
      window.clearTimeout(pollingRecoveryTimerRef.current);
      pollingRecoveryTimerRef.current = null;
    }
    try {
      const pendingBinding = readPendingTaskBinding(projectId);
      const activeTaskId = pendingBinding?.taskId || workflow?.activeTaskId;
      if (activeTaskId) {
        await authenticatedFetch(`${API_HOST}/api/adaptation-agent/projects/${projectId}/one-click-explainer/stop`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskId: activeTaskId, workflowStateKey: WORKFLOW_STATE_KEY }),
        }).catch(() => null);
      }
      const nextWorkflow = await stopNarrationWorkflow(projectId);
      clearPendingTaskBinding(projectId);
      setWorkflow(nextWorkflow);
      await refreshAssets().catch(() => {});
      setSubmitting(false);
      toast.info("流程已停止；已完成内容和当前阶段会保留");
    } catch (error) {
      toast.error(`停止失败：${error.message}`, 0);
    }
  };

  const handleResume = async () => {
    try {
      const nextWorkflow = await resumeNarrationWorkflow(projectId);
      setWorkflow(nextWorkflow);
      toast.info("已恢复到当前阶段，请手动点击继续");
    } catch (error) {
      toast.error(`恢复失败：${error.message}`, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[560px] items-center justify-center" style={{ backgroundColor: "var(--color-bg-primary)" }}>
        <Loader2 size={22} className="animate-spin" style={{ color: "var(--color-accent-primary)" }} />
      </div>
    );
  }

  if (!project || String(project.productionMode || "").trim().toLowerCase() !== "narration") {
    return (
      <div className="flex h-full min-h-[560px] items-center justify-center" style={{ backgroundColor: "var(--color-bg-primary)" }}>
        <div className="text-center">
          <AlertCircle size={28} className="mx-auto" style={{ color: "var(--color-warning)" }} />
          <div className="mt-3 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>当前项目不是解说剧项目</div>
          <ActionButton className="mt-4" onClick={() => navigate(`/project/${projectId}`)}>返回项目</ActionButton>
        </div>
      </div>
    );
  }

  const renderSource = () => (
    <section className="grid grid-cols-[minmax(0,1fr)_300px] gap-6 pt-6">
      <div className="min-w-0">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>解说剧剧本</h3>
            <p className="mt-1 text-[11px]" style={{ color: "var(--color-text-muted)" }}>支持 TXT、DOCX；保留旁白、画面、对白和集数标题。</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.docx,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(event) => handleFile(event.target.files?.[0])}
            />
            <ActionButton tone="secondary" disabled={importing || isProcessing} onClick={() => fileInputRef.current?.click()}>
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} 导入文件
            </ActionButton>
            <ActionButton tone="secondary" disabled={saving || isProcessing || !sourceDirty} onClick={handleSaveSource}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} 保存
            </ActionButton>
          </div>
        </div>
        <textarea
          value={sourceText}
          onChange={(event) => {
            setSourceText(event.target.value);
            setSourceDirty(true);
          }}
          disabled={isProcessing}
          className="min-h-[520px] w-full resize-y rounded-md border p-4 text-sm leading-7 outline-none custom-scrollbar"
          style={{
            borderColor: sourceDirty ? "var(--color-accent-primary)" : "var(--color-border-primary)",
            backgroundColor: "var(--color-bg-overlay)",
            color: "var(--color-text-primary)",
          }}
          placeholder="粘贴或导入解说剧剧本。角色在不同年龄、年代、服装、重生前后或受伤状态下的变化，可以直接写进原稿。"
        />
      </div>
      <aside className="border-l pl-6" style={{ borderColor: "var(--color-border-primary)" }}>
        <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>生产设置</h3>
        <div className="mt-4 space-y-4">
          <ProviderSelect label="文字模型" value={textProvider} onChange={setTextProvider} options={TEXT_AI_PROVIDERS} />
          <ProviderSelect label="图片模型" value={imageProvider} onChange={setImageProvider} options={IMAGE_AI_PROVIDERS} />
          <label className="block">
            <span className="mb-1.5 block text-[11px]" style={{ color: "var(--color-text-muted)" }}>项目画风</span>
            <AppSelect
              value={styleAnchor}
              onChange={(event) => setStyleAnchor(event.target.value)}
              className="h-10 w-full rounded-md border px-3 text-xs outline-none"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }}
            >
              {STYLE_OPTIONS.map((style) => <option key={style.value} value={style.value}>{style.label}</option>)}
            </AppSelect>
          </label>
        </div>
        <div className="mt-6 border-y py-4 text-[11px]" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="flex justify-between"><span style={{ color: "var(--color-text-muted)" }}>视频模型</span><span style={{ color: "var(--color-text-secondary)" }}>{getVideoProviderLabel(videoProvider)} Fast</span></div>
          <div className="mt-3 flex justify-between"><span style={{ color: "var(--color-text-muted)" }}>默认规格</span><span style={{ color: "var(--color-text-secondary)" }}>{videoResolution} · {ratio}</span></div>
        </div>
        <ActionButton
          className="mt-6 w-full"
          disabled={saving || isProcessing || !sourceText.trim()}
          onClick={handleSaveSource}
        >
          <Save size={15} /> 保存并进入分集确认
        </ActionButton>
      </aside>
    </section>
  );

  const renderEpisodeReview = () => (
    <section className="pt-6">
      <div className="flex items-center justify-between border-y py-4" style={{ borderColor: "var(--color-border-primary)" }}>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>分集预览</h3>
          <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>确认前可自由检查；确认后仅处理所选分集，其余内容保持不变。</p>
        </div>
        <div className="flex items-center gap-2">
          <AppSelect
            value={importMode}
            onChange={(event) => setImportMode(event.target.value)}
            className="h-10 rounded-md border px-3 text-xs"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }}
          >
            <option value="merge">同步同集正文</option>
            <option value="append">追加到末尾</option>
          </AppSelect>
          <ActionButton tone="secondary" disabled={Boolean(actionBusy)} onClick={handlePreviewEpisodes}>
            {actionBusy === "episodes-preview" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} 生成预览
          </ActionButton>
        </div>
      </div>
      {!previewRows.length ? (
        <EmptyHint
          icon={BookOpen}
          title="尚未生成分集预览"
          description="系统会按当前剧本中的集号和标题识别分集；单段文字会作为一集处理。"
          action={<ActionButton onClick={handlePreviewEpisodes}><WandSparkles size={14} /> 预览分集</ActionButton>}
        />
      ) : (
        <>
          <div className="mt-4 grid grid-cols-4 gap-3">
            <Stat label="现有集数" value={episodePreview.existingEpisodeCount || 0} />
            <Stat label="将新增" value={episodePreview.createCount || 0} tone="success" />
            <Stat label="将更新" value={episodePreview.updateCount || 0} tone="warning" />
            <Stat label="不变/跳过" value={(episodePreview.unchangedCount || 0) + (episodePreview.skippedCount || 0)} />
          </div>
          <div className="mt-4 divide-y rounded-md border" style={{ borderColor: "var(--color-border-primary)" }}>
            {previewRows.map((item) => {
              const target = Number(item.targetEpisodeNumber);
              const selectable = item.action === "CREATE" || item.action === "UPDATE";
              const selected = selectedEpisodeNumbers.includes(target);
              return (
                <label key={`${item.sourceEpisodeNumber}-${target}`} className="flex items-start gap-3 px-4 py-3.5">
                  <input
                    type="checkbox"
                    checked={selectable ? selected : false}
                    disabled={!selectable}
                    onChange={() => setSelectedEpisodeNumbers((previous) => (
                      previous.includes(target)
                        ? previous.filter((value) => value !== target)
                        : [...previous, target]
                    ))}
                    className="mt-1 accent-[var(--color-accent-primary)]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{item.title || `第 ${target} 集`}</span>
                      <span className="rounded px-1.5 py-0.5 text-[10px]" style={{ color: selectable ? "var(--color-accent-primary)" : "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}>{actionLabel(item.action)}</span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>{item.preview || item.note || "无预览"}</p>
                  </div>
                  <span className="shrink-0 text-[11px] font-mono-num" style={{ color: "var(--color-text-muted)" }}>{item.importedWordCount || 0} 字</span>
                </label>
              );
            })}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>{episodePreview.mappingNote || "只写入已勾选项。"}</p>
            <ActionButton disabled={Boolean(actionBusy)} onClick={handleConfirmEpisodes}>
              {actionBusy === "episodes-confirm" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} 确认分集并入库
            </ActionButton>
          </div>
        </>
      )}
    </section>
  );

  const renderAssetPlan = () => {
    const characters = normalizeList(assetBible.characters);
    const scenes = normalizeList(assetBible.scenes);
    const props = normalizeList(assetBible.props);
    const unresolved = normalizeList(assetBible.unresolved);
    const warnings = normalizeList(assetBible.warnings);
    const variantCount = characters.reduce((sum, item) => sum + normalizeList(item.variants).length, 0);
    const renderEmptyEditor = (title, description, collection) => (
      <EmptyHint
        icon={Sparkles}
        title={title}
        description={description}
        action={(
          <ActionButton tone="secondary" onClick={() => addAssetItem(collection)}>
            <Plus size={14} /> 新增一项
          </ActionButton>
        )}
      />
    );
    return (
      <section className="pt-6">
        <div className="flex items-center justify-between border-y py-4" style={{ borderColor: "var(--color-border-primary)" }}>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>全剧资产与角色变体圣经</h3>
            <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>一次读取已确认的全部分集；年龄、年代、重生、服装、伪装和受伤状态会拆成独立变体。</p>
          </div>
          <div className="flex gap-2">
            <ActionButton tone="secondary" disabled={Boolean(actionBusy)} onClick={handleAnalyzeBible}>
              {actionBusy === "asset-analyze" ? <Loader2 size={14} className="animate-spin" /> : <WandSparkles size={14} />} {characters.length ? "重新分析" : "AI 分析"}
            </ActionButton>
            <ActionButton tone="secondary" disabled={!assetBibleDirty || Boolean(actionBusy)} onClick={saveBible}>
              <Save size={14} /> 保存修改
            </ActionButton>
          </div>
        </div>
        {!characters.length && !scenes.length && !props.length ? (
          <EmptyHint icon={Sparkles} title="尚未分析全剧资产" description="先由 AI 找出角色、场景、道具和跨阶段人物变体，再由你确认后才会开始生图。" />
        ) : (
          <>
            <div className="mt-5 grid grid-cols-4 gap-3">
              <Stat label="角色" value={characters.length} />
              <Stat label="角色变体" value={variantCount} tone={variantCount ? "warning" : "default"} />
              <Stat label="场景" value={scenes.length} />
              <Stat label="道具" value={props.length} />
            </div>
            <div className="mt-5 overflow-hidden rounded-md border" style={{ borderColor: assetBibleDirty ? "var(--color-accent-primary)" : "var(--color-border-primary)" }}>
              <div className="flex items-center border-b" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
                <AssetEditorTab active={assetEditorTab === "characters"} count={characters.length} onClick={() => setAssetEditorTab("characters")}>角色与变体</AssetEditorTab>
                <AssetEditorTab active={assetEditorTab === "scenes"} count={scenes.length} onClick={() => setAssetEditorTab("scenes")}>场景</AssetEditorTab>
                <AssetEditorTab active={assetEditorTab === "props"} count={props.length} onClick={() => setAssetEditorTab("props")}>道具</AssetEditorTab>
                <AssetEditorTab active={assetEditorTab === "review"} count={unresolved.length + warnings.length} onClick={() => setAssetEditorTab("review")}>待确认</AssetEditorTab>
                {assetBibleDirty && (
                  <span className="ml-auto mr-4 text-[10px]" style={{ color: "var(--color-accent-primary)" }}>有未保存修改</span>
                )}
              </div>

              <div className="max-h-[650px] min-h-[360px] overflow-y-auto custom-scrollbar">
                {assetEditorTab === "characters" && (
                  characters.length === 0
                    ? renderEmptyEditor("还没有角色", "新增角色本体；年龄、重生前后、长期服装或身份变化请放在角色变体里。", "characters")
                    : (
                      <div className="divide-y" style={{ borderColor: "var(--color-border-primary)" }}>
                        {characters.map((character, characterIndex) => {
                          const variants = normalizeList(character.variants);
                          return (
                            <div key={`${character.name || "character"}-${characterIndex}`} className="p-5">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>角色 {characterIndex + 1}</span>
                                  <span className="ml-2 text-[10px]" style={{ color: "var(--color-text-muted)" }}>{variants.length} 个独立形象变体</span>
                                </div>
                                <button
                                  type="button"
                                  title="删除角色"
                                  onClick={() => removeAssetItem("characters", characterIndex)}
                                  className="flex h-8 w-8 items-center justify-center rounded-md border"
                                  style={{ borderColor: "var(--color-border-primary)", color: "var(--color-error)" }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                                <AssetEditorInput
                                  label="角色名"
                                  value={character.name}
                                  onChange={(value) => updateAssetItem("characters", characterIndex, "name", value)}
                                  placeholder="使用剧本中的正式称呼"
                                />
                                <AssetEditorInput
                                  label="别名"
                                  value={normalizeList(character.aliases).join("，")}
                                  onChange={(value) => updateAssetItem("characters", characterIndex, "aliases", value.split(/[,，、]/).map((item) => item.trim()).filter(Boolean))}
                                  placeholder="多个别名用逗号分隔"
                                />
                              </div>
                              <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
                                <AssetEditorTextarea
                                  label="稳定身份与人物关系"
                                  value={character.identity}
                                  onChange={(value) => updateAssetItem("characters", characterIndex, "identity", value)}
                                  placeholder="只写不随剧情阶段变化的身份"
                                />
                                <AssetEditorTextarea
                                  label="角色本体提示词"
                                  value={character.basePrompt}
                                  onChange={(value) => updateAssetItem("characters", characterIndex, "basePrompt", value)}
                                  placeholder="不要混入年龄变化、重生前后或多套服装"
                                />
                              </div>
                              <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--color-border-primary)" }}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>独立形象变体</div>
                                    <p className="mt-1 text-[10px]" style={{ color: "var(--color-text-muted)" }}>每个阶段生成单独图片，不会和人物本体画在同一张图中。</p>
                                  </div>
                                  <ActionButton tone="secondary" className="h-8 px-3" onClick={() => addCharacterVariant(characterIndex)}>
                                    <Plus size={13} /> 新增变体
                                  </ActionButton>
                                </div>
                                {variants.length > 0 && (
                                  <div className="mt-3 divide-y rounded-md border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                                    {variants.map((variant, variantIndex) => (
                                      <div key={`${variant.name || "variant"}-${variantIndex}`} className="p-4">
                                        <div className="grid grid-cols-[minmax(0,1fr)_150px_32px] items-end gap-3">
                                          <AssetEditorInput
                                            label="变体名称"
                                            value={variant.name}
                                            onChange={(value) => updateCharacterVariant(characterIndex, variantIndex, "name", value)}
                                            placeholder={`${character.name || "角色"}-阶段名`}
                                          />
                                          <label className="block">
                                            <span className="mb-1.5 block text-[10px]" style={{ color: "var(--color-text-muted)" }}>变化类型</span>
                                            <AppSelect
                                              value={variant.type || "state"}
                                              onChange={(event) => updateCharacterVariant(characterIndex, variantIndex, "type", event.target.value)}
                                              className="h-9 w-full rounded-md border px-2 text-xs outline-none"
                                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)", color: "var(--color-text-primary)" }}
                                            >
                                              <option value="age">年龄</option>
                                              <option value="era">年代</option>
                                              <option value="rebirth">重生前后</option>
                                              <option value="wardrobe">长期服装</option>
                                              <option value="disguise">伪装</option>
                                              <option value="injury">受伤状态</option>
                                              <option value="state">身份/状态</option>
                                            </AppSelect>
                                          </label>
                                          <button
                                            type="button"
                                            title="删除变体"
                                            onClick={() => removeCharacterVariant(characterIndex, variantIndex)}
                                            className="flex h-9 w-8 items-center justify-center rounded-md border"
                                            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-error)" }}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
                                          <AssetEditorTextarea
                                            label="使用条件"
                                            value={variant.trigger}
                                            onChange={(value) => updateCharacterVariant(characterIndex, variantIndex, "trigger", value)}
                                            placeholder="说明在哪些集、哪段时期使用"
                                            rows={2}
                                          />
                                          <AssetEditorTextarea
                                            label="该阶段完整外观提示词"
                                            value={variant.appearancePrompt}
                                            onChange={(value) => updateCharacterVariant(characterIndex, variantIndex, "appearancePrompt", value)}
                                            placeholder="只描述当前阶段，不要并列其他年龄或服装"
                                            rows={2}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <div className="p-4">
                          <ActionButton tone="secondary" onClick={() => addAssetItem("characters")}>
                            <Plus size={14} /> 新增角色
                          </ActionButton>
                        </div>
                      </div>
                    )
                )}

                {assetEditorTab === "scenes" && (
                  scenes.length === 0
                    ? renderEmptyEditor("还没有场景", "按剧本中的正式场景名建立可复用场景。", "scenes")
                    : (
                      <div className="divide-y" style={{ borderColor: "var(--color-border-primary)" }}>
                        {scenes.map((scene, index) => (
                          <div key={`${scene.name || "scene"}-${index}`} className="p-5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>场景 {index + 1}</span>
                              <button type="button" title="删除场景" onClick={() => removeAssetItem("scenes", index)} className="flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-error)" }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[280px_minmax(0,1fr)]">
                              <AssetEditorInput label="场景名" value={scene.name} onChange={(value) => updateAssetItem("scenes", index, "name", value)} placeholder="沿用剧本场景名" />
                              <AssetEditorInput label="稳定空间特征" value={scene.description} onChange={(value) => updateAssetItem("scenes", index, "description", value)} placeholder="说明不随镜头变化的空间结构" />
                            </div>
                            <div className="mt-3">
                              <AssetEditorTextarea label="场景生图提示词" value={scene.imagePrompt} onChange={(value) => updateAssetItem("scenes", index, "imagePrompt", value)} />
                            </div>
                          </div>
                        ))}
                        <div className="p-4"><ActionButton tone="secondary" onClick={() => addAssetItem("scenes")}><Plus size={14} /> 新增场景</ActionButton></div>
                      </div>
                    )
                )}

                {assetEditorTab === "props" && (
                  props.length === 0
                    ? renderEmptyEditor("还没有道具", "只保留剧情识别或动作依赖的关键道具。", "props")
                    : (
                      <div className="divide-y" style={{ borderColor: "var(--color-border-primary)" }}>
                        {props.map((prop, index) => (
                          <div key={`${prop.name || "prop"}-${index}`} className="p-5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>道具 {index + 1}</span>
                              <button type="button" title="删除道具" onClick={() => removeAssetItem("props", index)} className="flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-error)" }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
                              <AssetEditorInput label="道具名" value={prop.name} onChange={(value) => updateAssetItem("props", index, "name", value)} placeholder="剧本正式称呼" />
                              <AssetEditorInput label="所属人物" value={prop.owner} onChange={(value) => updateAssetItem("props", index, "owner", value)} placeholder="无固定所属可留空" />
                              <AssetEditorInput label="识别特征" value={prop.description} onChange={(value) => updateAssetItem("props", index, "description", value)} />
                            </div>
                            <div className="mt-3">
                              <AssetEditorTextarea label="道具生图提示词" value={prop.imagePrompt} onChange={(value) => updateAssetItem("props", index, "imagePrompt", value)} />
                            </div>
                          </div>
                        ))}
                        <div className="p-4"><ActionButton tone="secondary" onClick={() => addAssetItem("props")}><Plus size={14} /> 新增道具</ActionButton></div>
                      </div>
                    )
                )}

                {assetEditorTab === "review" && (
                  <div className="p-5">
                    <AssetEditorTextarea
                      label="全剧资产摘要"
                      value={assetBible.summary}
                      onChange={(value) => mutateAssetBible((draft) => { draft.summary = value; })}
                      placeholder="概括主要人物关系、场景范围和连续性重点"
                    />
                    {[
                      { key: "unresolved", title: "需要确认", items: unresolved, color: "var(--color-warning)", placeholder: "记录证据不足或需要人工决定的问题" },
                      { key: "warnings", title: "连续性提醒", items: warnings, color: "var(--color-error)", placeholder: "记录跨集形象、年代、道具等风险" },
                    ].map((group) => (
                      <div key={group.key} className="mt-5 border-t pt-4" style={{ borderColor: "var(--color-border-primary)" }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: group.color }}>
                            <AlertCircle size={14} /> {group.title}
                          </div>
                          <ActionButton tone="secondary" className="h-8 px-3" onClick={() => addReviewItem(group.key)}>
                            <Plus size={13} /> 添加
                          </ActionButton>
                        </div>
                        {group.items.length === 0 ? (
                          <p className="mt-3 text-xs" style={{ color: "var(--color-text-muted)" }}>暂无内容</p>
                        ) : (
                          <div className="mt-3 space-y-2">
                            {group.items.map((item, index) => (
                              <div key={`${group.key}-${index}`} className="flex gap-2">
                                <input
                                  value={item || ""}
                                  onChange={(event) => updateReviewItem(group.key, index, event.target.value)}
                                  placeholder={group.placeholder}
                                  className="h-9 min-w-0 flex-1 rounded-md border px-3 text-xs outline-none"
                                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }}
                                />
                                <button type="button" title="删除" onClick={() => removeReviewItem(group.key, index)} className="flex h-9 w-9 items-center justify-center rounded-md border" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-error)" }}>
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        <div className="mt-5 flex justify-end">
          <ActionButton disabled={!hasUsableAssetBible(assetBible) || Boolean(actionBusy)} onClick={handleConfirmBible}>
            <CheckCircle2 size={14} /> 确认规划并进入资产生成
          </ActionButton>
        </div>
      </section>
    );
  };

  const renderAssetReview = () => {
    const showGenerationProgress = isProcessing
      || generatedAssetTotal > 0
      || generatedVariantCount > 0
      || Boolean(workflow?.errorMessage);
    return (
      <section className="pt-6">
        {currentStage === "asset_preparation" ? (
          showGenerationProgress ? (
            <>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-4 border-y py-4" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {isProcessing ? <Loader2 size={15} className="animate-spin" /> : <PackageCheck size={15} />}
                    {isProcessing ? "资产正在逐项生成" : "当前资产生成结果"}
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap break-words text-xs leading-5" style={{ color: workflow?.errorMessage ? "var(--color-error)" : "var(--color-text-muted)" }}>
                    {isProcessing
                      ? progress.text || "正在生成角色、场景与道具"
                      : workflow?.errorMessage || "已生成的内容会保留，再次开始只补齐缺少的资产。"}
                  </p>
                  <div className="mt-3 h-1.5 max-w-xl overflow-hidden rounded-full" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    <div
                      className="h-full rounded-full transition-[width] duration-300"
                      style={{
                        width: `${Math.max(0, Math.min(100, Number(progress.progress || workflow?.progress || 0)))}%`,
                        backgroundColor: workflow?.errorMessage ? "var(--color-error)" : "var(--color-accent-primary)",
                      }}
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {assetGenerationSteps.map((step, index) => {
                      const tone = step.active
                        ? "var(--color-accent-primary)"
                        : step.warning
                          ? "var(--color-warning)"
                          : step.complete
                            ? "var(--color-success)"
                            : "var(--color-text-muted)";
                      return (
                        <div
                          key={step.key}
                          className="min-w-0 border px-2.5 py-2"
                          style={{
                            borderColor: step.active
                              ? "var(--color-accent-primary)"
                              : "var(--color-border-primary)",
                            backgroundColor: step.active
                              ? "color-mix(in srgb, var(--color-accent-primary) 8%, transparent)"
                              : "var(--color-bg-tertiary)",
                          }}
                        >
                          <div className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: tone }}>
                            {step.active ? (
                              <Loader2 size={12} className="shrink-0 animate-spin" />
                            ) : step.complete ? (
                              <Check size={12} className="shrink-0" />
                            ) : (
                              <span className="shrink-0 tabular-nums">{index + 1}</span>
                            )}
                            <span className="truncate">{step.label}</span>
                          </div>
                          <div className="mt-1 text-[10px] tabular-nums" style={{ color: "var(--color-text-muted)" }}>
                            {step.generated}/{step.planned}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <ActionButton tone="secondary" onClick={() => refreshAssets().catch((error) => toast.error(error.message, 0))}>
                    <RefreshCw size={14} /> 刷新资产
                  </ActionButton>
                  {!isProcessing && !isStopped && (
                    <ActionButton onClick={() => startStageTask("asset_preparation")}>
                      <WandSparkles size={14} /> 继续补齐
                    </ActionButton>
                  )}
                </div>
              </div>
              <AssetLinks projectId={projectId} assets={assets} navigate={navigate} />
              <div className="mt-4 flex flex-wrap items-center gap-6 border-y py-3" style={{ borderColor: "var(--color-border-primary)" }}>
                <Stat label="角色图片" value={`${generatedAssetCounts.characters}/${plannedAssetCounts.characters}`} tone={generatedAssetCounts.characters ? "success" : "default"} />
                <Stat label="角色变体" value={`${generatedVariantCount}/${plannedVariantCount}`} tone={plannedVariantCount === 0 ? "default" : missingVariantCount ? "warning" : "success"} />
                <Stat label="场景图片" value={`${generatedAssetCounts.scenes}/${plannedAssetCounts.scenes}`} tone={generatedAssetCounts.scenes ? "success" : "default"} />
                <Stat label="道具图片" value={`${generatedAssetCounts.props}/${plannedAssetCounts.props}`} tone={generatedAssetCounts.props ? "success" : "default"} />
              </div>
              {plannedVariantCount === 0 ? (
                <div className="mt-4 flex items-start gap-2 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}>
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  当前确认的资产规划中没有需要单独生图的角色变体，因此本轮只生成角色本体、场景和道具。
                </div>
              ) : (
                <div className="mt-4 text-xs leading-6" style={{ color: "var(--color-text-muted)" }}>
                  <p>已规划 {plannedVariantCount} 个角色变体，系统会在角色本体完成后逐个生成；当前完成 {generatedVariantCount} 个。</p>
                  <p className="mt-1 break-words">
                    计划：{plannedVariantLabels.slice(0, 8).join("、")}
                    {plannedVariantLabels.length > 8 ? ` 等 ${plannedVariantLabels.length} 个` : ""}
                  </p>
                </div>
              )}
              {!isProcessing && missingVariantCount > 0 && (
                <div className="mt-4 flex items-start gap-2 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-warning)", color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 8%, transparent)" }}>
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  仍有 {missingVariantCount} 个规划角色变体没有图片。请查看上方失败信息，或进入角色工坊检查主体图片和变体记录。
                </div>
              )}
              {characterVariantIssues.length > 0 && (
                <div className="mt-4 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-warning)", color: "var(--color-text-secondary)", backgroundColor: "color-mix(in srgb, var(--color-warning) 6%, transparent)" }}>
                  <div className="flex items-center gap-2 font-medium" style={{ color: "var(--color-warning)" }}>
                    <AlertCircle size={14} />
                    角色变体诊断
                  </div>
                  <ul className="mt-2 space-y-1">
                    {characterVariantIssues.slice(0, 6).map((issue, index) => (
                      <li key={`${issue}-${index}`} className="break-words">· {issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-4 text-xs leading-6" style={{ color: "var(--color-text-muted)" }}>
                资产生成期间会自动刷新。已成功的角色、变体、场景和道具会立即保留，不会因后续某一项失败而消失。
              </p>
            </>
          ) : (
            <EmptyHint
              icon={PackageCheck}
              title="资产规划已确认"
              description={`将按已确认的 ${episodes.length} 集剧本生成基础角色、独立形象变体、场景与道具。`}
              action={(
                <ActionButton disabled={isProcessing || isStopped} onClick={() => startStageTask("asset_preparation")}>
                  <WandSparkles size={14} /> 生成全部资产
                </ActionButton>
              )}
            />
          )
        ) : (
          <>
            <AssetLinks projectId={projectId} assets={assets} navigate={navigate} />
            <div className="mt-4 flex flex-wrap items-center gap-6 border-y py-3" style={{ borderColor: "var(--color-border-primary)" }}>
              <Stat label="角色图片" value={generatedAssetCounts.characters} tone={generatedAssetCounts.characters ? "success" : "default"} />
              <Stat label="角色变体" value={`${generatedVariantCount}/${plannedVariantCount}`} tone={plannedVariantCount === 0 ? "default" : missingVariantCount ? "warning" : "success"} />
              <Stat label="场景图片" value={generatedAssetCounts.scenes} tone={generatedAssetCounts.scenes ? "success" : "default"} />
              <Stat label="道具图片" value={generatedAssetCounts.props} tone={generatedAssetCounts.props ? "success" : "default"} />
            </div>
            {plannedVariantCount === 0 && (
              <div className="mt-4 flex items-start gap-2 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                本次资产规划没有识别出需要独立形象的角色变体。如果剧本中存在年龄、年代、重生、服装或身份变化，请返回资产规划补充后再生成。
              </div>
            )}
            {missingVariantCount > 0 && (
              <div className="mt-4 flex items-start gap-2 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-warning)", color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 8%, transparent)" }}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                仍有 {missingVariantCount} 个规划角色变体没有图片，请进入角色工坊检查主体名称、变体配置或失败记录。
              </div>
            )}
            {characterVariantIssues.length > 0 && (
              <div className="mt-4 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-warning)", color: "var(--color-text-secondary)", backgroundColor: "color-mix(in srgb, var(--color-warning) 6%, transparent)" }}>
                <div className="flex items-center gap-2 font-medium" style={{ color: "var(--color-warning)" }}>
                  <AlertCircle size={14} />
                  有 {characterVariantIssues.length} 个角色变体未生成
                </div>
                <ul className="mt-2 space-y-1">
                  {characterVariantIssues.slice(0, 6).map((issue, index) => (
                    <li key={`${issue}-${index}`}>· {issue}</li>
                  ))}
                </ul>
                {characterVariantIssues.length > 6 && (
                  <p className="mt-1" style={{ color: "var(--color-text-muted)" }}>
                    另有 {characterVariantIssues.length - 6} 条，请进入角色工坊继续检查。
                  </p>
                )}
              </div>
            )}
            {variantPlaceholderWarning && (
              <div className="mt-4 flex items-start gap-2 border-l-2 px-3 py-2 text-xs leading-5" style={{ borderColor: "var(--color-warning)", color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 8%, transparent)" }}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                {variantPlaceholderWarning}
              </div>
            )}
            <div className="mt-6 flex items-center justify-between gap-5">
              <p className="max-w-2xl text-xs leading-6" style={{ color: "var(--color-text-muted)" }}>请检查人物本体与各年龄、年代、服装等变体。缺图可以在对应工坊补生成，确认后才会进入分镜阶段。</p>
              <ActionButton disabled={isProcessing || Boolean(actionBusy)} onClick={handleConfirmAssets}>
                <CheckCircle2 size={14} /> 确认资产并继续
              </ActionButton>
            </div>
          </>
        )}
      </section>
    );
  };

  const episodeValidationRows = normalizeList(currentValidation?.episodes);
  const renderStoryboardReview = () => (
    <section className="pt-6">
      {currentStage === "storyboard_preparation" ? (
        <EmptyHint
          icon={Clapperboard}
          title="资产已确认"
          description="下一步只拆分各集的小故事板提示词，不生成视频；已有小故事板会保留并跳过。"
          action={(
            <ActionButton disabled={isProcessing || isStopped} onClick={() => startStageTask("storyboard_preparation")}>
              <Clapperboard size={14} /> 拆分全部分镜
            </ActionButton>
          )}
        />
      ) : (
        <>
          <div className="flex items-center justify-between border-y py-4" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-6">
              <Stat label="已检查集数" value={`${readyEpisodeCount}/${episodes.length}`} tone={readyEpisodeCount === episodes.length && episodes.length ? "success" : "default"} />
              <Stat label="阻塞项" value={blockingCount} tone={blockingCount > 0 ? "danger" : "success"} />
              <Stat label="提醒" value={warningCount} tone={warningCount > 0 ? "warning" : "default"} />
            </div>
            <ActionButton tone="secondary" disabled={Boolean(actionBusy)} onClick={handleValidateStoryboards}>
              {actionBusy === "storyboard-validate" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} 校验全部
            </ActionButton>
          </div>
          <div className="mt-3 divide-y" style={{ borderColor: "var(--color-border-primary)" }}>
            {episodes.map((episode) => {
              const row = episodeValidationRows.find((item) => String(item.episodeId) === String(episode.id));
              const issues = normalizeList(row?.issues);
              const hasError = issues.some((item) => item.level === "error");
              return (
                <button
                  key={episode.id}
                  type="button"
                  onClick={() => navigate(`/project/${projectId}/episode/${episode.id}/mini-storyboard`)}
                  className="group flex w-full items-center justify-between py-3.5 text-left"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ color: hasError ? "var(--color-error)" : row ? "var(--color-success)" : "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}>
                      {hasError ? <AlertCircle size={14} /> : <Check size={14} />}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>{episode.title || `第 ${episode.sortOrder || ""} 集`}</div>
                      <div className="mt-1 truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>
                        {row ? `${row.segmentCount || 0} 个片段 · ${issues.length ? issues.map((item) => item.message).join("；") : "提示词完整"}` : "尚未校验"}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={15} className="shrink-0 transition-transform group-hover:translate-x-1" style={{ color: "var(--color-text-muted)" }} />
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>提醒项只报告，不阻断；缺少剧本、分镜或视频提示词才会阻断。</p>
            <ActionButton disabled={blockingCount > 0 || Boolean(actionBusy)} onClick={handleConfirmStoryboards}>
              <CheckCircle2 size={14} /> 确认分镜
            </ActionButton>
          </div>
        </>
      )}
    </section>
  );

  const renderQa = () => (
    <section className="pt-6">
      <div className="grid grid-cols-4 border-y py-5" style={{ borderColor: "var(--color-border-primary)" }}>
        <Stat label="分集" value={episodes.length} />
        <Stat label="分镜片段" value={segmentTotal} />
        <Stat label="阻塞项" value={blockingCount} tone={blockingCount ? "danger" : "success"} />
        <Stat label="提醒" value={warningCount} tone={warningCount ? "warning" : "default"} />
      </div>
      <div className="mt-5 space-y-3">
        {episodeValidationRows.flatMap((episode) => normalizeList(episode.issues).map((issue, index) => (
          <button
            key={`${episode.episodeId}-${issue.code}-${issue.segmentIndex}-${index}`}
            type="button"
            onClick={() => navigate(`/project/${projectId}/episode/${episode.episodeId}/mini-storyboard`)}
            className="flex w-full items-start justify-between rounded-md border p-4 text-left"
            style={{ borderColor: issue.level === "error" ? "var(--color-error)" : "var(--color-border-primary)" }}
          >
            <div>
              <div className="text-xs font-semibold" style={{ color: issue.level === "error" ? "var(--color-error)" : "var(--color-warning)" }}>
                {episode.title || `第 ${episode.sortOrder || ""} 集`}{issue.segmentIndex ? ` · 片段 ${issue.segmentIndex}` : ""}
              </div>
              <p className="mt-1 text-xs" style={{ color: "var(--color-text-secondary)" }}>{issue.message}</p>
            </div>
            <ChevronRight size={15} style={{ color: "var(--color-text-muted)" }} />
          </button>
        )))}
        {!episodeValidationRows.some((episode) => normalizeList(episode.issues).length) && (
          <EmptyHint icon={ShieldCheck} title="质量校验已通过" description="所有分集都有剧本、小故事板和视频提示词，可以进入视频生产。" />
        )}
      </div>
      <div className="mt-5 flex items-center justify-between">
        <ActionButton tone="secondary" disabled={Boolean(actionBusy)} onClick={handleValidateStoryboards}><RefreshCw size={14} /> 重新校验</ActionButton>
        <ActionButton disabled={blockingCount > 0 || Boolean(actionBusy)} onClick={handleConfirmQa}><CheckCircle2 size={14} /> 确认校验并进入生产</ActionButton>
      </div>
    </section>
  );

  const renderDelivery = () => (
    <section className="pt-6">
      {currentStage === "production" ? (
        <EmptyHint
          icon={Video}
          title="准备生成视频"
          description="只处理尚未完成或失败的片段，已经生成成功的视频会跳过；中断后仍从当前完成位置继续。"
          action={<ActionButton disabled={isProcessing || isStopped} onClick={() => startStageTask("production")}><Play size={14} /> 生成缺失视频</ActionButton>}
        />
      ) : (
        <div className="flex min-h-[420px] flex-col items-center justify-center border-y text-center" style={{ borderColor: "var(--color-border-primary)" }}>
          <CheckCircle2 size={38} strokeWidth={1.4} style={{ color: "var(--color-success)" }} />
          <h3 className="mt-5 text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>解说剧已完成</h3>
          <p className="mt-2 max-w-xl text-xs leading-6" style={{ color: "var(--color-text-muted)" }}>已覆盖 {episodes.length} 集、{segmentTotal} 个片段。可进入任意一集的小故事板查看视频、历史版本和粗剪结果。</p>
          <div className="mt-6 flex max-w-3xl flex-wrap justify-center gap-2">
            {episodes.slice(0, 12).map((episode) => (
              <ActionButton key={episode.id} tone="secondary" onClick={() => navigate(`/project/${projectId}/episode/${episode.id}/mini-storyboard`)}>
                {episode.title || `第 ${episode.sortOrder || ""} 集`}
              </ActionButton>
            ))}
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      <header className="flex h-[72px] shrink-0 items-center justify-between border-b px-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ScrollText size={18} style={{ color: "var(--color-accent-primary)" }} />
            <h1 className="truncate text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>解说剧生产台</h1>
            <span className="rounded px-2 py-0.5 text-[10px] font-semibold" style={{ color: isOverseas ? "var(--color-success)" : "var(--color-accent-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}>
              {isOverseas ? `出海 · ${getVideoProviderLabel(videoProvider)}` : `国内 · ${getVideoProviderLabel(videoProvider)}`}
            </span>
          </div>
          <p className="mt-1 truncate text-[11px]" style={{ color: "var(--color-text-muted)" }}>{project.name} · 分集、资产规划、资产、分镜与校验逐步确认</p>
        </div>
        <div className="flex items-center gap-2">
          {isStopped ? (
            <ActionButton tone="secondary" onClick={handleResume}><Play size={14} /> 恢复流程</ActionButton>
          ) : isProcessing ? (
            <ActionButton tone="danger" onClick={handleStop}><Square size={13} /> 停止</ActionButton>
          ) : null}
          <ActionButton tone="secondary" title="刷新状态" className="w-10 px-0" onClick={() => refreshContext().catch((error) => toast.error(error.message, 0))}><RefreshCw size={14} /></ActionButton>
        </div>
      </header>

      {isProcessing && (
        <div className="relative h-1 shrink-0 overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
          <div className="h-full transition-[width] duration-500" style={{ width: `${Math.max(4, Math.min(100, progress.progress || 4))}%`, backgroundColor: "var(--color-accent-primary)" }} />
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <StageRail workflow={workflow} />
        <main className="min-w-0 flex-1 overflow-y-auto custom-scrollbar">
          <div className="mx-auto w-full max-w-[1480px] px-7 py-6">
            <section className="flex items-start justify-between gap-6 border-b pb-5" style={{ borderColor: "var(--color-border-primary)" }}>
              <div>
                <div className="text-[11px] font-semibold uppercase" style={{ color: "var(--color-accent-primary)" }}>当前阶段 · {String(currentIndex + 1).padStart(2, "0")}</div>
                <h2 className="mt-2 text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>{STAGE_TITLE[currentStage] || "解说剧生产"}</h2>
                <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-6" style={{ color: workflow?.errorMessage ? "var(--color-error)" : "var(--color-text-muted)" }}>
                  {isProcessing ? `${progress.text || "任务执行中"}；离开页面后仍会继续，回来可恢复查看。` : workflow?.errorMessage || "每一步都会自动保存，离开后可继续查看进度。"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-5">
                <Stat label="剧本集数" value={episodes.length} />
                <Stat label="分镜片段" value={segmentTotal} tone={segmentTotal ? "success" : "default"} />
                <Stat
                  label="状态"
                  value={workflow?.status === "completed"
                    ? "完成"
                    : workflow?.status === "submitting"
                      ? "提交中"
                      : workflow?.status === "analyzing"
                        ? "分析中"
                        : workflow?.status === "processing"
                          ? "运行中"
                        : workflow?.status === "failed"
                          ? "失败"
                        : workflow?.status === "stopped"
                          ? "已停止"
                          : "待操作"}
                />
              </div>
            </section>

            {currentIndex === 0 && renderSource()}
            {currentIndex === 1 && renderEpisodeReview()}
            {currentIndex === 2 && renderAssetPlan()}
            {currentIndex === 3 && renderAssetReview()}
            {currentIndex === 4 && renderStoryboardReview()}
            {currentIndex === 5 && renderQa()}
            {currentIndex === 6 && renderDelivery()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default NarrationProjectWorkbench;
